/**
 * CV HTML renderer — the single source of truth for both the on-screen
 * preview (PaginatedPreview) and PDF export (pdf-service via Puppeteer).
 *
 * Keep preview and PDF on the same HTML path so document output never drifts:
 * - `renderCVPage` / `getCVStyles` / `getAccentStyles` feed the live preview.
 * - `renderCVDocument` wraps the same markup + styles in a full HTML document
 *   for the PDF service.
 *
 * Templates are expressed as a `cv-template-${id}` class on `.cv-page` plus
 * accent CSS variables (`--cv-accent` / `--cv-accent-soft` / `--cv-section-title`).
 */

import { DEFAULT_CV_LOCALE, createTranslator, formatLocaleDate, resolveLocale } from '../i18n'
import type { CV } from '../types/cv'

type Translator = (key: string) => string

const SIDEBAR_TEMPLATES = new Set(['sidebar-left', 'sidebar-right'])
const KNOWN_TEMPLATES = new Set(['classic', 'modern', 'minimal', 'sidebar-left', 'sidebar-right'])

/**
 * Render the `.cv-page` element (template class + content), without surrounding
 * document chrome or styles. This is what the live preview measures and paints.
 */
export function renderCVPage(cv: CV): string {
  const { personalInfo } = cv
  const locale = resolveLocale(cv.locale || DEFAULT_CV_LOCALE)
  const t = createTranslator(locale)
  const templateId = normalizeTemplateId(cv.templateId)
  const isSidebarTemplate = SIDEBAR_TEMPLATES.has(templateId)
  const shouldShowPhoto = Boolean(personalInfo.photoUrl) && personalInfo.showPhoto !== false

  const headerHtml = renderHeader(cv, shouldShowPhoto, !isSidebarTemplate)
  const summaryHtml = isSidebarTemplate ? renderSummarySection(cv, t) : ''
  const experienceHtml = renderExperienceSection(cv, locale, t)
  const educationHtml = renderEducationSection(cv, locale, t)
  const skillsHtml = renderSkillsSection(cv, t)
  const languagesHtml = renderLanguagesSection(cv, t)
  const certificationsHtml = renderCertificationsSection(cv, locale, t)
  const customSectionsHtml = renderCustomSections(cv)

  const contentHtml = isSidebarTemplate
    ? `<aside class="cv-sidebar">\n${headerHtml}${skillsHtml}${languagesHtml}</aside>\n<main class="cv-main">\n${summaryHtml}${experienceHtml}${educationHtml}${certificationsHtml}${customSectionsHtml}</main>\n`
    : `${headerHtml}${experienceHtml}${educationHtml}${skillsHtml}${languagesHtml}${certificationsHtml}${customSectionsHtml}`

  return `<div class="cv-page cv-template-${templateId}">\n${contentHtml}</div>`
}

/**
 * Full standalone HTML document for PDF generation. Wraps the same page markup
 * and styles produced for the preview so output stays pixel-identical.
 */
export function renderCVDocument(cv: CV): string {
  const locale = resolveLocale(cv.locale || DEFAULT_CV_LOCALE)
  const accentStyles = getAccentStyles(cv.accentColor)
  const accentTag = accentStyles ? `\n  <style>${accentStyles}</style>` : ''

  return `
<!DOCTYPE html>
<html lang="${locale}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(cv.title)}</title>
  <style>${getCVStyles()}</style>${accentTag}
</head>
<body>
${renderCVPage(cv)}
</body>
</html>
`
}

function renderHeader(cv: CV, shouldShowPhoto: boolean, includeSummary: boolean): string {
  const { personalInfo } = cv

  let html = '<header class="cv-header">\n'
  if (shouldShowPhoto) {
    html += `<div class="cv-photo-frame"><img class="cv-photo" src="${escapeHtml(personalInfo.photoUrl)}" alt="" /></div>\n`
  }
  if (personalInfo.fullName) {
    html += `<h1 class="cv-name">${escapeHtml(personalInfo.fullName)}</h1>\n`
  }
  if (personalInfo.jobTitle) {
    html += `<p class="cv-job-title">${escapeHtml(personalInfo.jobTitle)}</p>\n`
  }

  html += '<div class="cv-contact">\n'
  if (personalInfo.email) {
    html += `<span class="cv-contact-item">${escapeHtml(personalInfo.email)}</span>\n`
  }
  if (personalInfo.phone) {
    html += `<span class="cv-contact-item">${escapeHtml(personalInfo.phone)}</span>\n`
  }
  if (personalInfo.city && personalInfo.country) {
    html += `<span class="cv-contact-item">${escapeHtml(personalInfo.city)}, ${escapeHtml(personalInfo.country)}</span>\n`
  }
  if (personalInfo.linkedin) {
    html += `<span class="cv-contact-item">${escapeHtml(personalInfo.linkedin)}</span>\n`
  }
  if (personalInfo.website) {
    html += `<span class="cv-contact-item">${escapeHtml(personalInfo.website)}</span>\n`
  }
  html += '</div>\n'

  if (includeSummary && personalInfo.summary) {
    html += `<p class="cv-summary">${escapeHtml(personalInfo.summary)}</p>\n`
  }
  html += '</header>\n'

  return html
}

function renderSummarySection(cv: CV, t: Translator): string {
  if (!cv.personalInfo.summary) return ''

  return `<section class="cv-section cv-section-summary">\n<h2 class="cv-section-title">${escapeHtml(t('cv.sections.profile'))}</h2>\n<p class="cv-summary">${escapeHtml(cv.personalInfo.summary)}</p>\n</section>\n`
}

function renderExperienceSection(cv: CV, locale: string, t: Translator): string {
  const { experiences } = cv
  if (experiences.length === 0) return ''

  let html = '<section class="cv-section cv-section-experience">\n'
  html += `<h2 class="cv-section-title">${escapeHtml(t('cv.sections.experience'))}</h2>\n`

  for (const exp of [...experiences].sort((a, b) => a.sortOrder - b.sortOrder)) {
    html += '<div class="cv-entry">\n'
    html += '<div class="cv-entry-header">\n'
    html += `<span class="cv-entry-title">${escapeHtml(exp.position)}</span>\n`
    html += `<span class="cv-entry-date">${formatDateRange(exp.startDate, exp.isCurrent ? null : exp.endDate, locale, t('cv.dates.present'))}</span>\n`
    html += '</div>\n'
    html += `<div class="cv-entry-subtitle">${escapeHtml(exp.company)}`
    if (exp.location) {
      html += ` • ${escapeHtml(exp.location)}`
    }
    html += '</div>\n'
    if (exp.description) {
      html += `<p class="cv-entry-description">${escapeHtml(exp.description)}</p>\n`
    }
    html += '</div>\n'
  }
  html += '</section>\n'

  return html
}

function renderEducationSection(cv: CV, locale: string, t: Translator): string {
  const { education } = cv
  if (education.length === 0) return ''

  let html = '<section class="cv-section cv-section-education">\n'
  html += `<h2 class="cv-section-title">${escapeHtml(t('cv.sections.education'))}</h2>\n`

  for (const edu of [...education].sort((a, b) => a.sortOrder - b.sortOrder)) {
    html += '<div class="cv-entry">\n'
    html += '<div class="cv-entry-header">\n'
    html += `<span class="cv-entry-title">${escapeHtml(edu.degree)}`
    if (edu.fieldOfStudy) {
      html += ` ${escapeHtml(t('cv.education.fieldConnector'))} ${escapeHtml(edu.fieldOfStudy)}`
    }
    html += '</span>\n'
    html += `<span class="cv-entry-date">${formatDateRange(edu.startDate, edu.isCurrent ? null : edu.endDate, locale, t('cv.dates.present'))}</span>\n`
    html += '</div>\n'
    html += `<div class="cv-entry-subtitle">${escapeHtml(edu.institution)}`
    if (edu.location) {
      html += ` • ${escapeHtml(edu.location)}`
    }
    if (edu.grade) {
      html += ` • ${escapeHtml(edu.grade)}`
    }
    html += '</div>\n'
    if (edu.description) {
      html += `<p class="cv-entry-description">${escapeHtml(edu.description)}</p>\n`
    }
    html += '</div>\n'
  }
  html += '</section>\n'

  return html
}

function renderSkillsSection(cv: CV, t: Translator): string {
  const { skills } = cv
  if (skills.length === 0) return ''

  let html = '<section class="cv-section cv-section-skills">\n'
  html += `<h2 class="cv-section-title">${escapeHtml(t('cv.sections.skills'))}</h2>\n`
  html += '<div class="cv-skills-grid">\n'

  for (const skill of [...skills].sort((a, b) => a.sortOrder - b.sortOrder)) {
    html += `<span class="cv-skill-tag" data-level="${escapeHtml(skill.level)}">${escapeHtml(skill.name)}</span>\n`
  }
  html += '</div>\n'
  html += '</section>\n'

  return html
}

function renderLanguagesSection(cv: CV, t: Translator): string {
  const { languages } = cv
  if (languages.length === 0) return ''

  let html = '<section class="cv-section cv-section-languages">\n'
  html += `<h2 class="cv-section-title">${escapeHtml(t('cv.sections.languages'))}</h2>\n`
  html += '<div class="cv-languages-list">\n'

  for (const lang of [...languages].sort((a, b) => a.sortOrder - b.sortOrder)) {
    html += '<div class="cv-language-item">\n'
    html += `<span class="cv-language-name">${escapeHtml(lang.name)}</span>\n`
    if (lang.proficiency) {
      html += `<span class="cv-language-proficiency"> (${escapeHtml(t(`cv.languageProficiency.${lang.proficiency}`))})</span>\n`
    }
    html += '</div>\n'
  }
  html += '</div>\n'
  html += '</section>\n'

  return html
}

function renderCertificationsSection(cv: CV, locale: string, t: Translator): string {
  const certifications = cv.certifications || []
  if (certifications.length === 0) return ''

  let html = '<section class="cv-section cv-section-certifications">\n'
  html += `<h2 class="cv-section-title">${escapeHtml(t('cv.sections.certifications'))}</h2>\n`

  for (const cert of [...certifications].sort((a, b) => a.sortOrder - b.sortOrder)) {
    html += '<div class="cv-entry">\n'
    html += '<div class="cv-entry-header">\n'
    html += `<span class="cv-entry-title">${escapeHtml(cert.name)}</span>\n`
    html += `<span class="cv-entry-date">${escapeHtml(formatDate(cert.issueDate, locale))}</span>\n`
    html += '</div>\n'
    html += `<div class="cv-entry-subtitle">${escapeHtml(cert.issuer)}`
    if (cert.credentialId) {
      html += ` • ${escapeHtml(t('cv.labels.credentialId'))}: ${escapeHtml(cert.credentialId)}`
    }
    html += '</div>\n'
    html += '</div>\n'
  }
  html += '</section>\n'

  return html
}

function renderCustomSections(cv: CV): string {
  const customSections = cv.customSections || []
  if (customSections.length === 0) return ''

  let html = ''

  for (const section of [...customSections].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (!section.content) continue

    html += '<section class="cv-section cv-section-custom">\n'
    html += `<h2 class="cv-section-title">${escapeHtml(section.title)}</h2>\n`
    html += '<div class="cv-custom-content">\n'
    const contentStr =
      typeof section.content === 'string' ? section.content : String(section.content)
    const content = escapeHtml(contentStr)
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .split('\n')
      .map((line) => `<p>${line}</p>`)
      .join('\n')
    html += content
    html += '</div>\n'
    html += '</section>\n'
  }

  return html
}

function escapeHtml(text: unknown): string {
  if (text === null || text === undefined) return ''
  const str = typeof text === 'string' ? text : String(text)
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

function formatDate(date: string | undefined | null, locale: string): string {
  if (!date) return ''
  return formatLocaleDate(date, locale, { month: 'short', year: 'numeric' })
}

function formatDateRange(
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  locale: string,
  presentLabel: string
): string {
  const start = formatDate(startDate, locale)
  const end = endDate ? formatDate(endDate, locale) : presentLabel

  if (!start && !end) return ''
  if (!start) return end
  if (!end) return start
  return `${start} – ${end}`
}

function normalizeTemplateId(templateId: string | undefined): string {
  if (templateId && KNOWN_TEMPLATES.has(templateId)) {
    return templateId
  }
  return 'classic'
}

/** Derive a light tint from a hex accent colour for use as --cv-accent-soft */
function hexToSoftRgb(hex: string): string {
  const clean = hex.replace('#', '')
  const r = Number.parseInt(clean.slice(0, 2), 16)
  const g = Number.parseInt(clean.slice(2, 4), 16)
  const b = Number.parseInt(clean.slice(4, 6), 16)
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#eef2f7'
  const mix = (c: number) => Math.round(c * 0.14 + 255 * 0.86)
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`
}

/**
 * CSS that overrides the page accent for a CV-specific accent colour, without a
 * surrounding `<style>` tag so callers can inline it however they need. Returns
 * an empty string when no valid hex colour is provided.
 */
export function getAccentStyles(accentColor: string | undefined): string {
  if (!accentColor || !/^#[0-9a-fA-F]{6}$/.test(accentColor)) return ''
  const soft = hexToSoftRgb(accentColor)
  const escaped = escapeHtml(accentColor)
  return `.cv-page{--cv-accent:${escaped};--cv-accent-soft:${soft};--cv-section-title:${escaped}}`
}

/**
 * The full CV stylesheet (no surrounding `<style>` tag). Shared by the preview
 * and the PDF document so templates render identically in both.
 */
export function getCVStyles(): string {
  return `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Segoe UI', system-ui, sans-serif;
      font-size: 12px;
      line-height: 1.5;
      color: #333;
      background: white;
    }

    .cv-page {
      --cv-accent: #334155;
      --cv-accent-soft: #eef2f7;
      --cv-section-title: #333;
      width: 100%;
      background: white;
      box-sizing: border-box;
    }

    .cv-template-modern {
      --cv-accent: #2563eb;
      --cv-accent-soft: #dbeafe;
      --cv-section-title: #1d4ed8;
    }

    .cv-template-minimal {
      --cv-accent: #94a3b8;
      --cv-accent-soft: #f8fafc;
      --cv-section-title: #111827;
    }

    .cv-template-sidebar-left,
    .cv-template-sidebar-right {
      --cv-accent: #0f766e;
      --cv-accent-soft: #ccfbf1;
      --cv-section-title: #0f766e;
      display: grid;
      grid-template-columns: 205px minmax(0, 1fr);
      gap: 22px;
      align-items: start;
    }

    .cv-template-sidebar-right {
      grid-template-columns: minmax(0, 1fr) 205px;
    }

    .cv-sidebar,
    .cv-main {
      min-width: 0;
    }

    .cv-sidebar {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .cv-template-sidebar-left .cv-sidebar {
      grid-column: 1;
    }

    .cv-template-sidebar-left .cv-main {
      grid-column: 2;
    }

    .cv-template-sidebar-right .cv-sidebar {
      grid-column: 2;
    }

    .cv-template-sidebar-right .cv-main {
      grid-column: 1;
      grid-row: 1;
    }

    .cv-header {
      text-align: center;
      margin-bottom: 20px;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .cv-template-modern .cv-header {
      text-align: left;
      padding-left: 18px;
      border-left: 5px solid var(--cv-accent);
    }

    .cv-template-minimal .cv-header {
      margin-bottom: 18px;
      padding-bottom: 14px;
      border-bottom: 1px solid color-mix(in srgb, var(--cv-accent) 22%, #e5e7eb);
    }

    .cv-sidebar .cv-header,
    .cv-sidebar .cv-section {
      margin-bottom: 0;
      padding: 14px;
      border-radius: 8px;
      background: var(--cv-accent-soft);
      border: 1px solid color-mix(in srgb, var(--cv-accent) 22%, transparent);
      break-inside: avoid;
      page-break-inside: avoid;
      text-align: left;
    }

    .cv-sidebar .cv-contact,
    .cv-sidebar .cv-languages-list,
    .cv-sidebar .cv-skills-grid {
      align-items: flex-start;
      flex-direction: column;
      justify-content: flex-start;
      gap: 7px;
    }

    .cv-template-sidebar-left .cv-summary,
    .cv-template-sidebar-right .cv-summary,
    .cv-section-summary .cv-summary {
      margin-top: 0;
      text-align: left;
    }

    .cv-template-sidebar-left .cv-section-title,
    .cv-template-sidebar-right .cv-section-title {
      font-size: 12px;
    }

    .cv-sidebar .cv-name {
      font-size: 22px;
      line-height: 1.12;
    }

    .cv-sidebar .cv-job-title {
      font-size: 13px;
      line-height: 1.35;
    }

    .cv-name {
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 4px 0;
      color: #1a1a1a;
    }

    .cv-job-title {
      font-size: 16px;
      color: #555;
      margin: 0 0 12px 0;
    }

    .cv-photo-frame {
      width: 102px;
      height: 102px;
      margin: 0 auto 12px;
      overflow: hidden;
      border: 3px solid white;
      border-radius: 999px;
      background: #e2e8f0;
      box-shadow: 0 0 0 1px #cbd5e1;
    }

    .cv-template-modern .cv-photo-frame {
      margin-left: 0;
    }

    .cv-template-sidebar-left .cv-photo-frame,
    .cv-template-sidebar-right .cv-photo-frame {
      width: 126px;
      height: 126px;
      margin-left: 0;
      margin-right: 0;
    }

    .cv-photo {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .cv-contact {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      font-size: 12px;
      color: #666;
    }

    .cv-contact-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .cv-summary {
      font-size: 12px;
      line-height: 1.6;
      color: #444;
      text-align: justify;
      margin-top: 12px;
    }

    .cv-section {
      margin-bottom: 16px;
    }

    .cv-section-title {
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 1.5px solid var(--cv-accent);
      padding-bottom: 4px;
      margin-bottom: 10px;
      color: var(--cv-section-title);
      break-after: avoid;
      page-break-after: avoid;
    }

    .cv-template-minimal .cv-section-title {
      font-size: 13px;
      text-transform: none;
      border-bottom-color: color-mix(in srgb, var(--cv-accent) 22%, #e5e7eb);
    }

    .cv-entry {
      margin-bottom: 14px;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .cv-entry-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 2px;
    }

    .cv-entry-title {
      font-size: 13px;
      font-weight: 600;
      color: #222;
    }

    .cv-entry-date {
      font-size: 11px;
      color: #666;
      white-space: nowrap;
    }

    .cv-entry-subtitle {
      font-size: 12px;
      color: #555;
      margin-bottom: 4px;
    }

    .cv-entry-description {
      font-size: 11px;
      line-height: 1.5;
      color: #444;
    }

    .cv-entry-description p {
      margin: 0 0 2px 0;
    }

    .cv-skills-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .cv-skill-tag {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 11px;
      padding: 3px 9px;
      background: var(--cv-accent-soft);
      border: 1px solid transparent;
      border-radius: 3px;
      color: #444;
    }

    /* level dot — a small filled circle whose opacity reflects skill depth */
    .cv-skill-tag::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.28;
      flex-shrink: 0;
    }

    .cv-skill-tag[data-level='expert'] {
      background: var(--cv-accent);
      color: #fff;
    }
    .cv-skill-tag[data-level='expert']::before {
      opacity: 0.7;
    }

    .cv-skill-tag[data-level='advanced'] {
      background: color-mix(in srgb, var(--cv-accent) 18%, var(--cv-accent-soft));
      border-color: color-mix(in srgb, var(--cv-accent) 30%, transparent);
      color: color-mix(in srgb, var(--cv-accent) 85%, #000);
    }
    .cv-skill-tag[data-level='advanced']::before {
      opacity: 0.55;
    }

    .cv-skill-tag[data-level='intermediate'] {
      background: var(--cv-accent-soft);
      border-color: color-mix(in srgb, var(--cv-accent) 20%, transparent);
      color: color-mix(in srgb, var(--cv-accent) 70%, #444);
    }
    .cv-skill-tag[data-level='intermediate']::before {
      opacity: 0.38;
    }

    .cv-languages-list {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .cv-language-item {
      font-size: 12px;
    }

    .cv-language-name {
      font-weight: 500;
    }

    .cv-language-proficiency {
      color: #666;
      font-size: 11px;
    }

    .cv-custom-content {
      font-size: 11px;
      line-height: 1.6;
      color: #444;
    }

    .cv-custom-content p {
      margin: 0 0 4px 0;
    }

    .cv-custom-content strong {
      color: #222;
      font-weight: 500;
    }

    @media print {
      body {
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `
}
